import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { CustomerData, EmbedTopOffset } from './embedScript';

const EMBED_SCRIPT_ID = 'bm-embed-v3-preview';
const SET_DEBOUNCE_MS = 300;
const WIDGET_BOOK_PATH = '/widget/v3';
const FRESH_PARAM = 'bmFresh=1';
const PREVIEW_LISTENER_ID = 'superadmin-preview';
const SELECTED_ARTICLES_EVENT = 'selectedArticlesChange';
const PRICE_QUANTITIES_EVENT = 'priceQuantitiesChange';

export type PreviewColorScheme = 'light' | 'dark';

export type PreviewSource =
    | { kind: 'inline'; configuration: Record<string, unknown> | null }
    | { kind: 'stored'; configurationId: string; language?: string; topOffset?: EmbedTopOffset };

export interface SelectedArticle {
    ArticleId: number;
    Quantity: number;
    ArticleTypeId: number | null;
    Duration: number;
}

export interface SelectedArticlesChangePayload {
    articles: SelectedArticle[];
}

export interface SetArticleQuantityCommand {
    articleId: number;
    quantity: number;
}

export interface PriceQuantity {
    PriceId: number;
    Quantity: number;
    Category: string | null;
    Price: number | null;
}

export interface PriceQuantitiesChangePayload {
    quantities: PriceQuantity[];
}

export interface SetPriceQuantityCommand {
    priceId: number;
    quantity: number;
}

export interface WidgetPreviewSessionOptions {
    source: PreviewSource;
    companySitepath: string;
    embedScriptUrl: string;
    colorScheme?: PreviewColorScheme;
    fitViewport?: boolean;
    onSelectedArticlesChange?: (payload: SelectedArticlesChangePayload) => void;
    onPriceQuantitiesChange?: (payload: PriceQuantitiesChangePayload) => void;
}

export interface WidgetPreviewSession {
    mountWidget: (node: HTMLDivElement | null) => void;
    sendArticleQuantity: (command: SetArticleQuantityCommand) => boolean;
    sendPriceQuantity: (command: SetPriceQuantityCommand) => boolean;
    sendCustomerData: (customerData: CustomerData) => boolean;
    isReady: boolean;
}

type BmCommand = unknown[];

type KeyRef = MutableRefObject<string | null>;

// The widget resolves dark vs light from `prefers-color-scheme`, which a
// cross-origin iframe inherits from the OS — the parent can't override it via
// CSS. To force a scheme we point the iframe at the widget URL with `?bmScheme`
// (handled by the SDK's `widgetUrl` opt). The widget origin equals the embed
// script origin (same subdomain host).
function buildWidgetUrl(embedScriptUrl: string, scheme?: PreviewColorScheme): string | undefined {
    if (!scheme || !embedScriptUrl) return undefined;
    try {
        return `${
            new URL(embedScriptUrl).origin
        }${WIDGET_BOOK_PATH}?bmScheme=${scheme}&${FRESH_PARAM}`;
    } catch {
        return undefined;
    }
}

// Unique key per widget mount so the SDK builds a fresh iframe; live edits then
// reach that iframe via the `set` command (postMessage), not a re-mount.
let previewKeySeq = 0;

const createPreviewKey = (): string => `preview-${(previewKeySeq += 1)}`;

const getQueue = (): BmCommand[] => {
    const w = window as unknown as { bmq?: BmCommand[] };
    if (!w.bmq) w.bmq = [];
    return w.bmq;
};

const ensureScript = (src: string): void => {
    if (document.getElementById(EMBED_SCRIPT_ID)) return;
    const script = document.createElement('script');
    script.id = EMBED_SCRIPT_ID;
    script.src = src;
    script.async = true;
    script.onerror = () => console.error('BokaMera: widget failed to load');
    document.head.appendChild(script);
};

export function useWidgetPreviewSession(
    options: WidgetPreviewSessionOptions
): WidgetPreviewSession {
    const { source, companySitepath, embedScriptUrl, colorScheme, fitViewport } = options;
    const keyRef = useRef<string | null>(null);
    const sourceRef = useRef(source);
    sourceRef.current = source;
    const listenerRef = useRef(options.onSelectedArticlesChange);
    listenerRef.current = options.onSelectedArticlesChange;
    const priceListenerRef = useRef(options.onPriceQuantitiesChange);
    priceListenerRef.current = options.onPriceQuantitiesChange;
    const [isReady, setIsReady] = useState(false);

    const forwardSelectedArticles = useCallback((payload: SelectedArticlesChangePayload) => {
        listenerRef.current?.(payload);
    }, []);

    const forwardPriceQuantities = useCallback((payload: PriceQuantitiesChangePayload) => {
        priceListenerRef.current?.(payload);
    }, []);

    const mountWidget = useCallback(
        (node: HTMLDivElement | null) => {
            if (!node) return teardownWidget(keyRef);
            if (!embedScriptUrl) return;
            ensureScript(embedScriptUrl);
            const key = createPreviewKey();
            keyRef.current = key;
            setIsReady(false);
            const onReady = () => keyRef.current === key && setIsReady(true);
            const widgetUrl = buildWidgetUrl(embedScriptUrl, colorScheme);
            getQueue().push([
                'config',
                key,
                {
                    kind: 'book',
                    companySitepath,
                    target: node,
                    fitViewport: Boolean(fitViewport),
                    ...(widgetUrl ? { widgetUrl } : {}),
                    onReady,
                    ...buildSourceOptions(sourceRef.current),
                },
            ]);
            pushListener(key, SELECTED_ARTICLES_EVENT, listenerRef.current, forwardSelectedArticles);
            pushListener(key, PRICE_QUANTITIES_EVENT, priceListenerRef.current, forwardPriceQuantities);
        },
        [
            embedScriptUrl,
            companySitepath,
            colorScheme,
            fitViewport,
            forwardSelectedArticles,
            forwardPriceQuantities,
        ]
    );

    const sendArticleQuantity = useCallback((command: SetArticleQuantityCommand): boolean => {
        if (!keyRef.current) return false;
        getQueue().push(['setArticleQuantity', keyRef.current, command]);
        return true;
    }, []);

    const sendPriceQuantity = useCallback((command: SetPriceQuantityCommand): boolean => {
        if (!keyRef.current) return false;
        getQueue().push(['setPriceQuantity', keyRef.current, command]);
        return true;
    }, []);

    const sendCustomerData = useCallback((customerData: CustomerData): boolean => {
        if (!keyRef.current) return false;
        getQueue().push(['setCustomerData', keyRef.current, customerData]);
        return true;
    }, []);

    useDebouncedCommand(keyRef, readInlineConfiguration(source), buildSetCommand);

    return { mountWidget, sendArticleQuantity, sendPriceQuantity, sendCustomerData, isReady };
}

const teardownWidget = (keyRef: KeyRef): void => {
    const key = keyRef.current;
    if (!key) return;
    getQueue().push(['off', key]);
    getQueue().push(['destroy', key]);
    keyRef.current = null;
};

const buildSourceOptions = (source: PreviewSource): Record<string, unknown> => {
    if (source.kind === 'inline') return { widgetConfiguration: source.configuration ?? {} };
    return omitUndefined({
        widgetConfigurationId: source.configurationId,
        language: source.language,
        topOffset: source.topOffset,
    });
};

const omitUndefined = (record: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined));

const pushListener = <TPayload,>(
    key: string,
    event: string,
    listener: ((payload: TPayload) => void) | undefined,
    forward: (payload: TPayload) => void
): void => {
    if (!listener) return;
    getQueue().push(['on', key, event, forward, { id: PREVIEW_LISTENER_ID }]);
};

const readInlineConfiguration = (source: PreviewSource): Record<string, unknown> | null =>
    source.kind === 'inline' ? source.configuration : null;

const buildSetCommand = (key: string, configuration: Record<string, unknown>): BmCommand => [
    'set',
    key,
    configuration,
];

function useDebouncedCommand<T>(
    keyRef: KeyRef,
    value: T | null,
    buildCommand: (key: string, value: T) => BmCommand
): void {
    const isFirstRunRef = useRef(true);
    useEffect(() => {
        if (isFirstRunRef.current) return void (isFirstRunRef.current = false);
        const key = keyRef.current;
        if (!key || !value) return;
        const timer = setTimeout(() => getQueue().push(buildCommand(key, value)), SET_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [keyRef, value, buildCommand]);
}

// Returns a callback ref: attach it to the container element where the embedded
// booking widget should render. The widget mounts once with the current config
// when the node attaches, then live-updates via `set` (postMessage) on config
// changes (debounced). null config skips updates (keeps the last good one).
export function useWidgetPreview(
    configuration: Record<string, unknown> | null,
    companySitepath: string,
    embedScriptUrl: string,
    forceColorScheme?: PreviewColorScheme,
    fitViewport?: boolean
): (node: HTMLDivElement | null) => void {
    return useWidgetPreviewSession({
        source: { kind: 'inline', configuration },
        companySitepath,
        embedScriptUrl,
        colorScheme: forceColorScheme,
        fitViewport,
    }).mountWidget;
}
